import React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from '../../models'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import styles from './OrderForm.module.css'
import Modal from '../Modal'
import CustomerDetails from '../orderForm/CustomerDetails'
import Delivery from '../orderForm/Delivery'
import StoreComment from '../orderForm/StoreComment'
import RadioButton from '../RadioButton'
import Input from '../Input'
import Banner from '../Banner'
import CustomerComment from '../orderForm/CustomerComment'
import AddProduct from '../orderForm/AddProduct'
import CateringItemDetail from '../CateringItemDetail'
import CloseIcon from '../../icons/Close'
import UpdateProduct from '../orderForm/UpdateProduct'
import { OpenHour } from '../../types/Store'
import { Order, OrderItem } from '../../types/Order'
import { DeliveryMethod } from '../../types/DeliveryMethod'
import { Product } from '../../types/Product'
import { getDayOfWeek } from '../../helpers/getWeekday'
import { getFormattedPrice } from '../../helpers/formatPrice'
import {
  useCustomerForm,
  useDeliveryForm,
  useProductsManagement,
  useComments,
  useFormValidation,
  useModalState,
} from './useCreateOrderState'
import {
  useProductOperations,
  useDeliveryMethods,
} from './useCreateOrderOperations'
import { useUnsavedChanges, validateAndScrollToError } from './useCreateOrderValidation'
import {
  calculateProductsSubTotal,
  getArticleText,
  buildOrderPayload,
  scrollToRef,
  DELIVERY_NAMES,
} from './createOrderHelpers'

interface Props {
  active: boolean
  openHours?: OpenHour[]
  onCancel: () => void
  onOrderCreated?: (order: Order) => void
  createOrder: (payload: any) => Promise<Order | undefined>
  getCateringProduct: (productId: string) => Promise<Product | undefined>
  deliveryMethods: DeliveryMethod[]
}

function CreateOrder({
  active,
  onCancel,
  onOrderCreated,
  openHours = [],
  createOrder,
  getCateringProduct,
  deliveryMethods,
}: Props) {
  const { storeId } = useParams<string>()

  // Custom hooks for state management
  const customerForm = useCustomerForm()
  const deliveryForm = useDeliveryForm()
  const productsManagement = useProductsManagement()
  const comments = useComments()
  const validation = useFormValidation()
  const modalState = useModalState()

  // Refs for scrolling
  const customerRef = React.useRef<HTMLDivElement | null>(null)
  const bannerRef = React.useRef<HTMLDivElement | null>(null)
  const deliveryRef = React.useRef<HTMLDivElement | null>(null)
  const productsRef = React.useRef<HTMLDivElement | null>(null)
  const updateProductRef = React.useRef<HTMLDivElement | null>(null)
  const homeDeliveryInfoRef = React.useRef<HTMLDivElement | null>(null)
  const deliveryMethodRef = React.useRef<HTMLDivElement | null>(null)

  // Computed values
  const dayOfWeek = format(deliveryForm.deliveryDate, 'yyyy-MM-dd')
    ? getDayOfWeek(format(deliveryForm.deliveryDate, 'yyyy-MM-dd'))
    : ''

  const productsSubTotal = React.useMemo(
    () => calculateProductsSubTotal(productsManagement.productsToAdd),
    [productsManagement.productsToAdd]
  )

  const deliveryFee = deliveryForm.selectedDeliveryMethod?.price ?? 0
  
  const totalPrice = React.useMemo(
    () => productsSubTotal + deliveryFee,
    [productsSubTotal, deliveryFee]
  )

  const articleText = React.useMemo(
    () => getArticleText(productsManagement.productsToAdd),
    [productsManagement.productsToAdd]
  )

  const memoizedProductToUpdate = React.useMemo(
    () => productsManagement.productToUpdate as OrderItem | null,
    [
      productsManagement.productToUpdate?.id,
      productsManagement.productToUpdate?.amount,
      productsManagement.productToUpdate?.variantId,
      productsManagement.productToUpdate?.message,
      productsManagement.productToUpdate?.priceGross,
      productsManagement.productToUpdate?.vatSubTotal,
    ]
  )

  const hasUnsavedChanges = useUnsavedChanges({
    customerName: customerForm.customerName,
    customerPhone: customerForm.customerPhone,
    customerEmail: customerForm.customerEmail,
    productsToAdd: productsManagement.productsToAdd,
    storeComment: comments.storeComment,
    customerComment: comments.customerComment,
    deliveryTimeSlot: deliveryForm.deliveryTimeSlot,
    selectedDeliveryMethod: deliveryForm.selectedDeliveryMethod,
    deliveryAddress: deliveryForm.deliveryAddress,
    zipCode: deliveryForm.zipCode,
    city: deliveryForm.city,
    showAddProduct: productsManagement.showAddProduct,
    updateProduct: productsManagement.updateProduct,
    deliveryDate: deliveryForm.deliveryDate,
  })

  const orderButtonTitle = React.useMemo(() => {
    if (modalState.isCreatingOrder) return 'Skapar order...'
    if (productsManagement.showAddProduct) return 'Lägg till produkt'
    if (productsManagement.updateProduct) return 'Ändra produkt'
    return 'Lägg till order'
  }, [
    productsManagement.showAddProduct,
    productsManagement.updateProduct,
    modalState.isCreatingOrder,
  ])

  
  // Reset all form state
  const reset = () => {
    customerForm.reset()
    deliveryForm.reset()
    productsManagement.reset()
    comments.reset()
    validation.reset()
  }

  // Handle cancel with unsaved changes check
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      modalState.setShowUnsavedModal(true)
      return
    }
    reset()
    onCancel()
  }

  const confirmDiscard = () => {
    modalState.setShowUnsavedModal(false)
    reset()
    onCancel()
  }

  const confirmSave = async () => {
    modalState.setShowUnsavedModal(false)
  }

  
  // Handle creating the order
  const handleAddOrder = async () => {
    modalState.setIsCreatingOrder(true)
    validation.setShowValidation(true)

    const isValid = validateAndScrollToError({
      customerName: customerForm.customerName,
      customerEmail: customerForm.customerEmail,
      customerPhone: customerForm.customerPhone,
      deliveryTimeSlot: deliveryForm.deliveryTimeSlot,
      productsToAdd: productsManagement.productsToAdd,
      selectedDeliveryMethod: deliveryForm.selectedDeliveryMethod,
      deliveryAddress: deliveryForm.deliveryAddress,
      zipCode: deliveryForm.zipCode,
      city: deliveryForm.city,
      refs: {
        customerRef,
        productsRef,
        deliveryRef,
        deliveryMethodRef,
        homeDeliveryInfoRef,
      },
      callbacks: {
        setIsEmailValid: customerForm.setIsEmailValid,
        setIsPhoneValid: customerForm.setIsPhoneValid,
        setDeliveryTimeSlotError: deliveryForm.setDeliveryTimeSlotError,
        setAddProductError: productsManagement.setAddProductError,
        setDeliveryInformationError: deliveryForm.setDeliveryInformationError,
        setDeliveryMethodError: deliveryForm.setDeliveryMethodError,
      },
    })

    if (!isValid) {
      modalState.setIsCreatingOrder(false)
      return
    }

    const payload = buildOrderPayload({
      storeId: storeId as string,
      customerName: customerForm.customerName,
      customerEmail: customerForm.customerEmail,
      customerPhone: customerForm.customerPhone,
      deliveryAddress: deliveryForm.deliveryAddress,
      zipCode: deliveryForm.zipCode,
      city: deliveryForm.city,
      deliveryDate: deliveryForm.deliveryDate,
      deliveryTimeSlot: deliveryForm.deliveryTimeSlot as string,
      selectedDeliveryMethod: deliveryForm.selectedDeliveryMethod as any,
      customerComment: comments.customerComment,
      storeComment: comments.storeComment,
      productsToAdd: productsManagement.productsToAdd,
    })

    try {
      validation.setCreateOrderError(null)
      const orderCreated = await createOrder(payload)

      reset()
      onCancel()

      if (orderCreated && onOrderCreated) {
        onOrderCreated(orderCreated)
      }
    } catch (error: any) {
      console.error('createOrder failed', error)
      validation.setCreateOrderError('Ett fel uppstod vid skapandet av ordern')
      scrollToRef(bannerRef, { behavior: 'smooth', block: 'center' })
    } finally {
      modalState.setIsCreatingOrder(false)
    }
  }

  // Product operations
  const productOperations = useProductOperations({
    storeId,
    getCateringProduct,
    setProductsToAdd: productsManagement.setProductsToAdd,
    setProductToUpdate: productsManagement.setProductToUpdate,
    setShowAddProduct: productsManagement.setShowAddProduct,
    setUpdateProduct: productsManagement.setUpdateProduct,
    setAddProductError: productsManagement.setAddProductError,
    setCreateOrderError: validation.setCreateOrderError,
    customerRef,
  })

  const handleAddProduct = () => productOperations.handleAddProduct(productsManagement.productToAdd)
  const handleUpdateProduct = () => productOperations.handleUpdateProduct(productsManagement.productToUpdate)
  const handleAmountChangeForUpdate = (newCount: number | string, id: string) =>
    productOperations.handleAmountChange(newCount, id)
  const handleSelectVariantForUpdate = (
    orderItemId: string,
    productPrice: number,
    taxCode: number,
    variant: any
  ) => productOperations.handleSelectVariant(orderItemId, productPrice, taxCode, variant)
  const handleCustomerCommentChangeForUpdate = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    productOperations.handleCustomerCommentChange(e)

  // Helper for closing modals and scrolling
  const closeAndScroll = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setter(false)
    scrollToRef(customerRef, { behavior: 'smooth', block: 'center' })
  }

  const handleCancelFromAddProduct = () => closeAndScroll(productsManagement.setShowAddProduct)
  const handleCancelFromUpdateProduct = () => {
    productsManagement.setProductToUpdate(null)
    closeAndScroll(productsManagement.setUpdateProduct)
  }

  // Delivery methods hook with auto-selection logic
  const handleMethodsLoaded = React.useCallback((methods: DeliveryMethod[]) => {
    // Auto-select when there's only one delivery method
    if (methods.length === 1) {
      const [method] = methods
      deliveryForm.setSelectedDeliveryMethod(method)
    }
  }, [deliveryForm.setSelectedDeliveryMethod])

  // Auto-select single delivery method when methods load
  React.useEffect(() => {
    if (deliveryMethods.length > 0 && !deliveryForm.selectedDeliveryMethod) {
      handleMethodsLoaded(deliveryMethods)
    }
  }, [deliveryMethods, deliveryForm.selectedDeliveryMethod, handleMethodsLoaded])

  const { hasMultipleDeliveryMethods } = useDeliveryMethods(
    deliveryMethods
  )

  // Reset validation when modal becomes active
  React.useEffect(() => {
    if (active) {
      validation.setShowValidation(false)
    }
  }, [active])

  return (
    <Modal active={active} onCancel={handleCancel} maxWidth='810px'>
      <div className={styles.modalWrapper}>
        <div className={styles.modalContainer}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>Skapa ny order</h3>
          </div>
          {!productsManagement.showAddProduct && !productsManagement.updateProduct && (
            <div className={styles.createOrderInfo} ref={bannerRef}>
              <Banner theme="info">
                <ul className={styles.createOrderList}>
                  <li>
                    En nyskapad order blir belagd med betalsätt &quot;betala i butik&quot;
                  </li>
                  <li>
                    Kunden får ett bekräftelsemail när ordern skapats
                  </li>
                  <li>
                    Första möjliga leveransdag kan tidigast vara nästkommande dag
                  </li>
              
                </ul>
              </Banner>
            </div>
          )}
          {validation.createOrderError && (
            <div style={{ margin: '0rem 1.5rem' }}>
              <Banner theme="warning">{validation.createOrderError}</Banner>
            </div>
          )}
          {!productsManagement.showAddProduct && !productsManagement.updateProduct && (
            <div className={styles.modalBodyDefault}>
              <div className={styles.modalBody}>
                <div ref={customerRef}>
                  <CustomerDetails
                    customerName={customerForm.customerName}
                    customerEmail={customerForm.customerEmail}
                    customerPhone={customerForm.customerPhone}
                    onInputChange={customerForm.handleInputChange}
                    validNameProp={validation.showValidation ? customerForm.customerName.length > 0 : true}
                    validPhoneProp={validation.showValidation ? customerForm.isPhoneValid : true}
                    onPhoneValidation={customerForm.setIsPhoneValid}
                    validEmailProp={validation.showValidation ? customerForm.isEmailValid : true}
                    onEmailValidation={customerForm.setIsEmailValid}
                    noBottomSpacing={true}
                    headingText='Kunduppgifter'
                    headingSize='1.15rem'

                  />
                </div>
                <div className={styles.addProductSection} style={productsManagement.productsToAdd.length === 0 ? {   borderTop: '1px solid var(--graySpecial)',
                  borderBottom: '1px solid var(--graySpecial)' } : {}} ref={productsRef}>
                  <h4 className={styles.heading} style={{marginBottom: '1rem'}}>Välj produkter att lägga till</h4>
                  <button
                    type='button'
                    onClick={() => productsManagement.setShowAddProduct(true)}
                    className={styles.buttonPrimary}
                  >
                    + Lägg till produkt
                  </button>
                  {productsManagement.addProductError && <p className={styles.errorMessage}>Lägg till minst en produkt</p>}
                </div>
                {productsManagement.productsToAdd.length > 0 && (
                  <div className={styles.orderItemsContainer}>
                    {productsManagement.productsToAdd.map((product, index) => (
                      <CateringItemDetail
                        key={index}
                        item={product}
                        editMode={true}
                        editProduct={productsManagement.toggleEditProduct}
                        onDelete={productsManagement.handleDeleteProduct}
                      />
                    ))}
                  </div>
                )}
                {productsManagement.productsToAdd.length > 0 && (
                  <div className={styles.priceDetails}>
                    <div className={styles.itemContent}>
                      <div>
                        <span className={styles.totalLabel}>Totalt</span>
                      </div>
                      <div className={styles.priceRight}>
                        <span className={styles.totalPrice}>
                          {getFormattedPrice(totalPrice)}
                        </span>
                      </div>
                    </div>

                    <div className={styles.itemContent}>
                      <div>
                        <span className={styles.numberOfArticles}>{articleText}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div style={{paddingBottom: '1rem'}} ref={deliveryRef}>
                  <Delivery
                    deliveryDate={deliveryForm.deliveryDate.toISOString()}
                    deliveryTimeSlot={deliveryForm.deliveryTimeSlot}
                    deliveryTimeSlotError={deliveryForm.deliveryTimeSlotError}
                    dayOfWeek={dayOfWeek}
                    openHours={openHours}
                    calenderIsVisible={true}
                    setDeliveryTimeSlotError={deliveryForm.setDeliveryTimeSlotError}
                    onUpdateDeliveryDate={deliveryForm.setDeliveryDate}
                    onUpdateDeliveryTimeSlot={deliveryForm.setDeliveryTimeSlot}
                    noBottomSpacing={true}
                    headingText='Välj dag och tid för leverans'
                    paymentType={'InStore'}
                    bongCreatedOrder={true}
                  />
                </div>
                {hasMultipleDeliveryMethods && (
                  <div className={styles.radioSectionWrapper} ref={deliveryMethodRef}>
                    <h4 className={styles.heading}>Leveranssätt</h4>
                    <div className={`${styles.radioGroup} ${styles.noBottomSpacing}`}> 
                      {deliveryMethods.map((method) => (
                        <RadioButton
                          key={method.id}
                          id={method.id}
                          name='delivery'
                          value={method.id}
                          checked={deliveryForm.selectedDeliveryMethod?.id === method.id}
                          onChange={() => deliveryForm.onDeliverySelection(method)}
                        >
                          {method.name}
                        </RadioButton>
                      ))}
                      {deliveryForm.deliveryMethodError && <p className={styles.errorMessage}>Välj ett leveranssätt</p>}
                      {deliveryForm.selectedDeliveryMethod?.deliveryType === DELIVERY_NAMES.HOME_DELIVERY && (
                        <div className={styles.deliveryInputsGroup} ref={homeDeliveryInfoRef}>
                          <Input 
                            value={deliveryForm.deliveryAddress} 
                            onChange={(e) => deliveryForm.setDeliveryAddress(e.target.value)} 
                            error={deliveryForm.deliveryInformationError && !deliveryForm.deliveryAddress} 
                            errorText='Ange en gatuadress' 
                            type='text' 
                            id='address' 
                            placeholder='Gatuadress'
                            required={true} 
                            noSpacing={true}
                          />
                          <Input 
                            value={deliveryForm.zipCode} 
                            onChange={(e) => deliveryForm.setZipCode(e.target.value)} 
                            error={deliveryForm.deliveryInformationError && !deliveryForm.zipCode} 
                            errorText='Ange ett postnummer' 
                            type='text' 
                            id='zipCode' 
                            placeholder='Postnummer'
                            required={true} 
                            noSpacing={true}
                          />
                          <Input 
                            value={deliveryForm.city} 
                            onChange={(e) => deliveryForm.setCity(e.target.value)} 
                            error={deliveryForm.deliveryInformationError && !deliveryForm.city} 
                            errorText='Ange en stad' 
                            type='text' 
                            id='city' 
                            placeholder='Stad'
                            required={true} 
                            noSpacing={true}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className={styles.radioSectionWrapper}>
                  <h4 className={styles.heading}>Betalsätt</h4>
                  <div className={`${styles.radioGroup} ${styles.noBottomSpacing}`}>
                    <RadioButton id='inStore' checked={true}>
                      Betala i butik
                    </RadioButton>
                  </div>
                </div>
                <div className={styles.commentWrapper}>
                  <div>
                    <CustomerComment
                      localCustomerComment={comments.customerComment}
                      onCustomerCommentChange={(e) => comments.setCustomerComment(e.target.value)}
                      noBottomSpacing={true}
                    />
                  </div>
                  <div>
                    <StoreComment
                      localStoreComment={comments.storeComment}
                      originalStoreComment={''}
                      onStoreCommentChange={(e) => comments.setStoreComment(e.target.value)}
                      noBottomSpacing={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {productsManagement.showAddProduct && (
            <AddProduct 
              storeId={storeId}
              setProductToAdd={productsManagement.setProductToAdd}
              noBottomSpacing={true}      
            />
          )}
          {productsManagement.updateProduct && (
            <div ref={updateProductRef} className={styles.productUpdateSection}>
              <h4 className={styles.productUpdateHeading}>Ändra produkt</h4>
              <UpdateProduct 
                key={memoizedProductToUpdate?.id}
                orderItem={memoizedProductToUpdate as OrderItem}
                onAmountChange={handleAmountChangeForUpdate}
                onSelectVariant={handleSelectVariantForUpdate}
                error={null}
                customerComment={memoizedProductToUpdate?.message || ''}
                onCustomerCommentChange={handleCustomerCommentChangeForUpdate}
                noBottomSpacing={true}
              /> 
            </div>
          )} 
          <div className={styles.modalFooter}>
            <button
              type='button'
              onClick={
                productsManagement.showAddProduct 
                  ? handleCancelFromAddProduct 
                  : productsManagement.updateProduct 
                    ? handleCancelFromUpdateProduct 
                    : handleCancel
              }
              className={styles.buttonSecondary}
            >
              Avbryt
            </button>
            <button
              type='button'
              onClick={
                productsManagement.showAddProduct 
                  ? handleAddProduct 
                  : productsManagement.updateProduct 
                    ? handleUpdateProduct 
                    : handleAddOrder
              }
              className={styles.buttonPrimary}
              disabled={modalState.isCreatingOrder || (productsManagement.showAddProduct && !productsManagement.productToAdd) }
            >
              {orderButtonTitle}
            </button>
          </div>
          <Modal
            active={modalState.showUnsavedModal}
            onCancel={() => modalState.setShowUnsavedModal(false)}
            withClose={false}
            maxWidth='762px'
            zIndex='150'
          >
            <div className={styles.modalWrapper}>
              <div className={styles.modalContainerSmall}>
                <div className={styles.confirmModalInfo}>
                  <button
                    className={styles.modalCloseButtonAlt}
                    onClick={() => modalState.setShowUnsavedModal(false)}
                    type='button'
                  >
                    <CloseIcon className={styles.iconClose} color='var(--redMid)' />
                  </button>
                  <h3 className={styles.confirmModalTitle}>
                    Är du säker på att du vill avbryta?
                  </h3>
                  <p className={styles.confirmModalDescription}>
                    Du har påbörjat en order. Om du avbryter kommer ändringar inte att sparas.
                  </p>
                </div>
                <div className={styles.confirmModalActions}>
                  <button
                    className={styles.confirmModalButton}
                    id={styles.confirmYes}
                    onClick={() => { confirmSave() }}
                  >
                    Fortsätt skapa order
                  </button>
                  <button
                    className={styles.confirmModalButton}
                    id={styles.confirmNo}
                    onClick={() => { confirmDiscard() }}
                  >
                    Avbryt utan att spara
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </Modal>
  )
}

function mapDispatch (dispatch: Dispatch) {
  return {
    getCateringProduct: dispatch.order.getCateringProduct,
    createOrder: dispatch.order.createOrder,
  }
}

export default connect(null, mapDispatch)(CreateOrder)